import React, { useState, useEffect } from "react";
import { Organization, User } from "@pollyslack/hub/src/types";
import { MarketingDmsTable } from "../../User/components/MarketingDmsTable";
import { LifecycleMessagesTable } from "../../User/components/LifecycleMessagesTable";
import { OthersMessagesTable } from "../../User/components/OthersMessagesTable";
import * as UserAPI from "../../../services/api/User";
import * as SlackMessagesAPI from "../../../services/api/SlackMessages";

export const MessagesTab: React.FC<{ organization: Organization }> = ({
  organization,
}) => {
  const [currentTab, setCurrentTab] = useState<
    "marketing" | "lifecycle" | "others"
  >("marketing");
  const [users, setUsers] = useState<User[]>([]);
  const [otherMessages, setOtherMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (organization) {
      setIsLoading(true);
      UserAPI.list({ search: organization._id })
        .then((users) => {
          setUsers(users);
        })
        .catch((e) => {
          alert(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [organization]);

  const loadOtherMessages = async () => {
    setIsLoading(true);
    try {
      const users = await UserAPI.list({ search: organization._id });

      const nudgeMessageIds =
        users
          .flatMap((u) =>
            u.profile?.nudgeMessages?.map((m) => m.messageId).filter(Boolean),
          )
          .filter(Boolean) || [];

      const marketingMessageIds =
        users
          .flatMap((u) =>
            u.profile?.marketingMessages?.flatMap((c) =>
              c.messages?.map((m) => m.messageId),
            ),
          )
          .filter(Boolean)
          .flat() || [];

      const excludedMessageIds = [
        ...new Set([...nudgeMessageIds, ...marketingMessageIds]),
      ];

      const response = await SlackMessagesAPI.list({
        orgId: organization._id,
        exclude: excludedMessageIds as string[],
      });
      setOtherMessages(response);
    } catch (e) {
      alert(e);
    } finally {
      setIsLoading(false);
    }
  };

  const onTabClick = (tab: "marketing" | "lifecycle" | "others") => {
    setCurrentTab(tab);
    if (tab === "others" && otherMessages.length === 0) {
      loadOtherMessages();
    }
  };

  const subTabClass = (tab: string) => {
    if (currentTab === tab) {
      return "nav-link active";
    }
    return "nav-link";
  };

  return (
    <div>
      <ul className="nav nav-tabs">
        <li className="nav-item">
          <a
            className={subTabClass("marketing")}
            onClick={() => onTabClick("marketing")}
          >
            Marketing
          </a>
        </li>
        <li className="nav-item">
          <a
            className={subTabClass("lifecycle")}
            onClick={() => onTabClick("lifecycle")}
          >
            Lifecycle
          </a>
        </li>
        <li className="nav-item">
          <a
            className={subTabClass("others")}
            onClick={() => onTabClick("others")}
          >
            Others
          </a>
        </li>
      </ul>
      <div className="mt-2">
        {currentTab === "marketing" && (
          <MarketingDmsTable organization={organization} />
        )}
        {currentTab === "lifecycle" && (
          <LifecycleMessagesTable organization={organization} />
        )}
        {currentTab === "others" && (
          <OthersMessagesTable
            messages={otherMessages}
            users={users}
            showUser={true}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};
